import { createSelector } from '@reduxjs/toolkit';
import { JURISDICTION_VIEW } from '@rsos/capstone/src/constants/capabilities';
import {
  selectCapstoneAudioSettingsForFeature,
  selectUserAudioSettingsForFeature,
} from '@rsos/sinatra/src/selectors/selectAudioSettings';
import { CapstoneAudioSettings } from '@rsos/sinatra/src/types/Organization';
import { playTone } from './audioHelpers';
import {
  AudioNotificationsGlobalState,
  AudioNotificationStatus,
  NOTIFICATION_COMPLETE,
} from './audioNotificationTypes';
import { selectHasAgent511Access, selectIsSMS } from './smsHelpers';

// There are different audio notifications settings for different kinds of
// calls; this says which one to use for this call.
const selectAudioFeatureForCall = createSelector(
  selectIsSMS,
  selectHasAgent511Access,
  (isSMS, has511) => {
    if (isSMS && has511) return 'tt911';
    if (isSMS) return 'sms';
    return 'calls';
  },
);

export async function playCallNotificationIfNecessary(
  state: AudioNotificationsGlobalState,
  callerID: string,
  repeater: () => void,
) {
  // The default condition is "done"
  const status: AudioNotificationStatus = NOTIFICATION_COMPLETE();

  const removedCalls = state.emergencyCalls.removedCalls.map(c => c.caller_id);
  const isTT911 = state.emergencyCalls.callQueue?.[callerID]?.isTT911;
  const hasJurisdictionView =
    state.psaps.currentPSAP.activeCapabilities[JURISDICTION_VIEW];

  if (removedCalls.includes(callerID) || !hasJurisdictionView) return status;

  // AUDIO-NOTIFICATIONS TODO: skip notifying on filtered calls to avoid
  // alerting on invisible items. This is optional -- the current system does
  // not filter notifications this way -- but it's weird to have a notification
  // sound and nothing actionable onscreen.
  //
  // const filters = state.emergencyCalls.callFilters
  // if (this call is filtered) return status;

  const feature = selectAudioFeatureForCall(state, callerID);
  const orgSettings = selectCapstoneAudioSettingsForFeature(feature)(state);
  const userSettings = selectUserAudioSettingsForFeature(feature)(state);

  // If the sound is turned off, mark it notified
  if (!orgSettings.sound_on) return status;

  // IF YOU GOT HERE, ACTUALLY PLAY THE SOUND
  await playTone(orgSettings.tone, userSettings.volume, repeater);

  // LEGACY FEATURE SUPPORT HAX:
  // The 'tt911' feature is the name for the old Agent511 settings :( Agent511
  // settings allow notifications to repeat a fixed number of of times if the
  // user hasn't opened the queue item. Delete this when the last customer stops
  // using Agent511!
  if (feature == 'tt911' && isTT911) {
    const agent511Settings =
      orgSettings as Required<CapstoneAudioSettings>['tt911'];
    if (agent511Settings.repeat) {
      const existingStatus = state.audioNotifications.calls[callerID];
      // minus 1 because we're playing it the first time, right now.
      status.repeatsRemaining = existingStatus
        ? existingStatus.repeatsRemaining - 1 // repeat: decrement
        : agent511Settings.repeat_times - 1; // first call: initialize
    }
  }

  return status;
}
